import { memo, useMemo } from 'react';
import { CHART_CONFIG } from '../config';

type ColorScheme = 'green' | 'blue';

interface ProgressBarProps {
  label: string;
  value: number;
  color: ColorScheme;
  scaleLabels: {
    low: string;
    mid: string;
    high: string;
  };
}

export const ProgressBar = memo(({ label, value, color, scaleLabels }: ProgressBarProps) => {
  const { progressPercentage, barColor } = useMemo(
    () => ({
      progressPercentage: Math.max(0, Math.min(100, value * 100)),
      barColor: color === 'green' ? CHART_CONFIG.COLORS.SUCCESS : CHART_CONFIG.COLORS.SECONDARY,
    }),
    [value, color],
  );

  return (
    <div className="space-y-2 rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface)] p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[var(--anl-text)]">{label}</span>
        <span className="text-lg font-bold" style={{ color: barColor }}>
          {value.toFixed(3)}
        </span>
      </div>
      <div className="relative">
        <div className="bg-[var(--anl-border)]/50 h-3 w-full rounded-full">
          <div
            className="h-3 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%`, backgroundColor: barColor }}
          />
        </div>
        <div className="mt-1 flex justify-between text-xs text-[var(--anl-text-muted)]">
          <span>{scaleLabels.low}</span>
          <span>{scaleLabels.mid}</span>
          <span>{scaleLabels.high}</span>
        </div>
        <div className="mt-0.5 flex justify-between text-xs text-[var(--anl-text-muted)]">
          <span>0.0</span>
          <span>0.5</span>
          <span>1.0</span>
        </div>
      </div>
    </div>
  );
});

export default ProgressBar;
